import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

const WINDOW = QUOTED.EX_DATE_AVOID_SESSIONS;

export const exDateAvoid: WikiTopic = {
  slug: "ex-date-avoid",
  title: "Ex-date avoid",
  summary: `A bonus or split ex-date in the last ${WINDOW} sessions, a window that has lagged the market.`,
  group: "strategies",
  horizon: "position",
  strategyKey: "corporate_action",
  sections: [
    {
      heading: "What it flags",
      code: "corporate_action_avoid",
      blocks: [
        p(
          <>
            Every stock that went ex-bonus or ex-split in the last <Datum>{WINDOW}</Datum> sessions.
            The flag goes up on the ex-date and stays up through the {WINDOW}th session, counting
            the ex-date as the first. Dividends, rights, and buybacks are not flagged.
          </>,
        ),
        p(
          <>
            The window is counted in trading sessions, not calendar days, so a holiday inside it
            does not end the flag a day early.
          </>,
        ),
      ],
    },
    {
      heading: "Why it exists",
      blocks: [
        p(
          <>
            The 2026-10-18 event study measured liquid NSE stocks over the {WINDOW} sessions after
            the ex-date, against the equal-weight universe. About 70% of them lagged it.
          </>,
        ),
        table(
          "Underperformance over the window, by action and period",
          ["Action", "2006-15", "2016-26"],
          [
            ["Bonus", <Datum key="b1">-5.9%</Datum>, <Datum key="b2">-4.8%</Datum>],
            ["Split", <Datum key="s1">-7.0%</Datum>, <Datum key="s2">-6.9%</Datum>],
          ],
        ),
        note(
          <>
            The same stocks 6 and 12 months away showed nothing. The flag is about the month after
            the ex-date, not about the company.
          </>,
        ),
      ],
    },
    {
      heading: "Who hears about it",
      blocks: [
        p(
          <>
            The row is <Datum>warn</Datum> for a stock in one of your open long trades and{" "}
            <Datum>info</Datum> for the rest. Only a holder gets it on Telegram: to them it is a
            reason to sell, and to everyone else it is a reason to wait, which belongs in the app.
          </>,
        ),
        p(
          <>
            The screen, the symbol page, and the trades page show a badge on every flagged stock
            with the session it is on, such as <Datum>split 3/{WINDOW}</Datum>.
          </>,
        ),
        note(
          <>
            Holdings are the trades entered on the trades page. There is no broker sync, so a stock
            you hold but never entered gets the <Datum>info</Datum> row only.
          </>,
        ),
      ],
    },
    {
      heading: "What the row carries",
      blocks: [
        p(
          <>
            Side is <Datum>short</Datum>, because a fall is what the flag predicts, so outcomes
            count the stock's fall as the flag being right. Stop and target are <Datum>null</Datum>:
            the flag is a window, not a trade.
          </>,
        ),
        p(
          <>
            The row is filed on the ex-date. The job re-reads the whole window every evening, so a
            bonus ingested late still fires on the next run, and the other re-runs write nothing.
          </>,
        ),
      ],
    },
  ],
};
